import { useTranslation } from 'react-i18next'
import { useState } from 'react'

interface FeaturedPatent {
  imgId: string
  title: string
  patentNumber: string
  imgBackUrl: string
}

interface PatentsSectionProps {
  title: string
  titleHighlight: string
  description: string
  notice: string
  backgroundImageUrl: string
  cardBackImageUrl: string
  flagIconUrl: string
  cards: FeaturedPatent[]
}

const PATENT_FRONT =
  'https://d2ql3aoebfyj3b.cloudfront.net/event/summer-promotion-2507/patent-section/04-img-patent'

function PatentCard({
  card,
  flagIconUrl,
  cardBackImageUrl,
  flipped,
  setFlipped,
}: {
  card: FeaturedPatent
  flagIconUrl: string
  cardBackImageUrl: string
  flipped: boolean
  setFlipped: (v: boolean) => void
}) {
  const { t: mt } = useTranslation('marketing')

  return (
    <div
      className="relative cursor-pointer w-[184px] h-[264px] lg:w-[264px] lg:h-[378px]"
      style={{ perspective: '1000px' }}
      onClick={() => setFlipped(!flipped)}
      onMouseEnter={() => setFlipped(true)}
      onMouseLeave={() => setFlipped(false)}
    >
      <div
        className="relative w-full h-full transition-transform duration-700"
        style={{
          transformStyle: 'preserve-3d',
          transform: flipped ? 'rotateY(180deg)' : 'rotateY(0deg)',
        }}
      >
        {/* Front */}
        <div
          className="absolute inset-0 bg-[#f0f0f0] rounded-lg overflow-hidden"
          style={{ backfaceVisibility: 'hidden' }}
        >
          <img
            src={`${PATENT_FRONT}${card.imgId}@2x.png`}
            alt={card.title}
            className="w-full h-full object-cover rounded-lg"
          />
        </div>

        {/* Back */}
        <div
          className="
            absolute inset-0 bg-black rounded-lg
            border border-white/10
            shadow-[0px_24px_12px_0px_rgba(0,0,0,0.7),inset_0px_-4px_4px_0px_rgba(255,255,255,0.3)]
            bg-cover bg-center bg-no-repeat
            flex flex-col p-4 lg:p-8
          "
          style={{
            backfaceVisibility: 'hidden',
            transform: 'rotateY(180deg)',
            backgroundImage: `url(${cardBackImageUrl})`,
          }}
        >
          <div className="flex flex-col gap-2 lg:gap-4 w-full">
            <div
              className="w-8 h-8 bg-no-repeat bg-center bg-contain"
              style={{ backgroundImage: `url(${flagIconUrl})` }}
              aria-hidden
            />
            <p className="font-normal text-[12px] lg:text-[14px] leading-[1.5] tracking-[-0.6px] text-white text-left m-0">
              {mt('patentOffice.korea')}<br />({card.patentNumber})
            </p>
            <p className="w-full font-semibold text-[14px] lg:text-[18px] leading-[1.5] tracking-[-0.6px] text-white text-left m-0">
              {card.title}
            </p>
          </div>
          <div className="mt-auto flex items-center justify-center">
            <img
              src={card.imgBackUrl}
              alt={card.title}
              className="w-[152px] h-[82px] lg:w-[216px] lg:h-[117px] border-4 border-[#3380e6] rounded-[4px] object-cover"
            />
          </div>
        </div>
      </div>
    </div>
  )
}

export function PatentsSection({
  title,
  titleHighlight,
  description,
  notice,
  backgroundImageUrl,
  cardBackImageUrl,
  flagIconUrl,
  cards,
}: PatentsSectionProps) {
  const [flipped, setFlipped] = useState<Record<number, boolean>>({})

  return (
    <section id="patents" data-marketing-section="" className="relative w-full bg-[#030411] overflow-hidden flex flex-col items-center gap-14 lg:gap-[104px] py-14 lg:py-[104px] px-0 lg:px-10">
      {/* Background starfield */}
      <div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[1920px] h-[960px] bg-no-repeat bg-center bg-[length:100%_100%] pointer-events-none"
        style={{ backgroundImage: `url(${backgroundImageUrl})` }}
        aria-hidden
      />

      {/* Content */}
      <div className="relative z-10 flex flex-col items-stretch w-full max-w-[1136px]">
        {/* Title block */}
        <div className="flex flex-col items-stretch gap-2 lg:gap-4 px-5 lg:px-0">
          <h2 className="font-bold text-[28px] lg:text-[40px] leading-[1.4] lg:leading-[1.2] tracking-[-0.6px] text-white text-center">
            {title}
          </h2>
          <h2 className="font-bold text-[28px] lg:text-[40px] leading-[1.4] lg:leading-[1.2] tracking-[-0.6px] text-qb-primary text-center">
            {titleHighlight}
          </h2>
        </div>

        <p className="mt-4 lg:mt-8 mb-4 lg:mb-8 font-normal text-[16px] lg:text-[20px] leading-[1.5] tracking-[-0.6px] text-white text-center whitespace-pre-line px-5 lg:px-0">
          {description}
        </p>

        <p className="font-normal text-[14px] leading-[1.5] tracking-[-0.6px] text-white text-center px-5 lg:px-0">
          {notice}
        </p>

        {/* Cards */}
        <div className="mt-14 lg:mt-[104px] flex justify-center items-center gap-4 lg:gap-12 flex-wrap lg:flex-nowrap">
          {cards.map((card, idx) => (
            <PatentCard
              key={idx}
              card={card}
              flagIconUrl={flagIconUrl}
              cardBackImageUrl={cardBackImageUrl}
              flipped={flipped[idx] ?? false}
              setFlipped={(v) =>
                setFlipped((prev) => ({ ...prev, [idx]: v }))
              }
            />
          ))}
        </div>
      </div>
    </section>
  )
}
